import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  HomeIcon, 
  CalendarIcon, 
  TruckIcon, 
  BuildingOfficeIcon,
  BuildingStorefrontIcon,
  DocumentTextIcon,
  ChartBarIcon,
  UsersIcon,
  BanknotesIcon,
  CogIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  BellIcon,
  UserCircleIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../contexts/AuthContext';
import { useVendor } from '../../contexts/VendorContext';
import AdminLayout from './AdminLayout';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  // Move all hooks to the top before any conditional returns
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const { currentVendor, setCurrentVendor } = useVendor();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Clear vendor context for super_admin users
  React.useEffect(() => {
    if (user && user.role === 'super_admin' && currentVendor) {
      console.log('🔍 Clearing vendor context for super_admin user');
      setCurrentVendor(null);
      localStorage.removeItem('currentVendor');
    }
  }, [user, currentVendor, setCurrentVendor]);
  
  // Render admin layout if user is system super_admin
  if (user && user.role === 'super_admin') {
    console.log('🔍 Rendering AdminLayout for super_admin user');
    return <AdminLayout>{children}</AdminLayout>;
  }

  // Determine if current user is vendor or admin
  const isVendor = currentVendor || (user && user.role !== 'super_admin');
  const baseUrl = isVendor ? '/vendor' : '';

  const navigation = [
    { name: 'لوحة التحكم', href: isVendor ? '/vendor' : '/', icon: HomeIcon, current: location.pathname === (isVendor ? '/vendor' : '/') },
    { name: 'الحجوزات', href: `${baseUrl}/bookings`, icon: CalendarIcon, current: location.pathname.startsWith('/bookings') || location.pathname.startsWith('/vendor/bookings') },
    { name: 'الرحلات', href: `${baseUrl}/trips`, icon: TruckIcon, current: location.pathname.startsWith('/trips') || location.pathname.startsWith('/vendor/trips') },
    { name: 'الباصات', href: `${baseUrl}/buses`, icon: BuildingOfficeIcon, current: location.pathname.startsWith('/buses') || location.pathname.startsWith('/vendor/buses') },
    { name: 'السكن', href: `${baseUrl}/accommodations`, icon: BuildingStorefrontIcon, current: location.pathname.startsWith('/accommodations') || location.pathname.startsWith('/vendor/accommodations') },
    { name: 'إغلاق الكاشير', href: `${baseUrl}/cash-closing`, icon: BanknotesIcon, current: location.pathname.startsWith('/cash-closing') || location.pathname.startsWith('/vendor/cash-closing') },
    { name: 'التقارير', href: `${baseUrl}/reports`, icon: ChartBarIcon, current: location.pathname.startsWith('/reports') || location.pathname.startsWith('/vendor/reports') },
    { name: 'المستخدمين', href: `${baseUrl}/users`, icon: UsersIcon, current: location.pathname.startsWith('/users') || location.pathname.startsWith('/vendor/users') },
    ...(user?.role === 'super_admin' ? [{ name: 'البائعون', href: '/vendors', icon: BuildingStorefrontIcon, current: location.pathname.startsWith('/vendors') }] : []),
    { name: 'الإعدادات', href: `${baseUrl}/settings`, icon: CogIcon, current: location.pathname.startsWith('/settings') || location.pathname.startsWith('/vendor/settings') },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-gray-50 rtl">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 flex z-40 md:hidden ${sidebarOpen ? '' : 'hidden'}`}>
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
        <div className="relative flex-1 flex flex-col max-w-xs w-full bg-white">
          <div className="absolute top-0 left-0 -ml-12 pt-2">
            <button
              type="button"
              className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="h-6 w-6 text-white" />
            </button>
          </div>
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex-shrink-0 flex items-center px-4">
              <h1 className="text-xl font-bold text-primary-600">مكتب الحرمين</h1>
            </div>
            <nav className="mt-5 px-2 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${item.current ? 'active' : ''}`}
                  onClick={() => setSidebarOpen(false)}
                >
                  <item.icon className="ml-3 h-6 w-6" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="flex items-center w-full text-gray-700 hover:text-red-600 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="ml-3 h-6 w-6" />
              تسجيل الخروج
            </button>
          </div>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:w-64 md:flex-col md:fixed md:inset-y-0">
        <div className="flex-1 flex flex-col min-h-0 border-l border-gray-200 bg-white">
          <div className="flex-1 flex flex-col pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1 className="text-2xl font-bold text-primary-600">مكتب الحرمين</h1>
            </div>
            <nav className="mt-8 flex-1 px-2 space-y-2">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`sidebar-item ${item.current ? 'active' : ''}`}
                >
                  <item.icon className="ml-3 h-6 w-6" />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex-shrink-0 flex border-t border-gray-200 p-4">
            <button
              onClick={handleLogout}
              className="flex items-center w-full text-gray-700 hover:text-red-600 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="ml-3 h-6 w-6" />
              تسجيل الخروج
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="md:pr-64 flex flex-col flex-1">
        {/* Top header */}
        <div className="sticky top-0 z-10 md:hidden pr-1 pt-1 pb-1 sm:pr-3 sm:pt-3 bg-gray-50">
          <button
            type="button"
            className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-gray-500 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={() => setSidebarOpen(true)}
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
        </div>

        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  {navigation.find(item => item.current)?.name || 'لوحة التحكم'}
                </h2>
              </div>
              
              <div className="flex items-center space-x-4 space-x-reverse">
                {/* Notifications */}
                <button className="p-2 text-gray-400 hover:text-gray-600 transition-colors">
                  <BellIcon className="h-6 w-6" />
                </button>
                
                {/* User menu */}
                <div className="flex items-center space-x-3 space-x-reverse">
                  <div className="flex items-center space-x-2 space-x-reverse">
                    <UserCircleIcon className="h-8 w-8 text-gray-400" />
                    <div className="text-sm">
                      <p className="font-medium text-gray-900">{user?.fullName}</p>
                      <p className="text-gray-500">{user?.branch?.name}</p>
                      {currentVendor && (
                        <p className="text-xs text-gray-400">{currentVendor.vendorName}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Main content area */}
        <main className="flex-1">
          <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout; 